import React, { useEffect, useState, useMemo } from "react";
import { IconButton, Tooltip, Chip, CircularProgress, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import PageviewIcon from '@mui/icons-material/Pageview';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Delete } from "@mui/icons-material";
import { DataTable, Loadable } from '../../../../common';
import {
    Portlet,
    PortletBody,
    PortletHeader,
} from "../../../../partials/content/Portlet";
import ContractDocumentDeleteButton from "../ContractDocumentDelete/ContractDocumentDelete";
import { useData } from "@pure-ptr/react";
import { ContractInfo } from "../../../../store/api/ai-api";

const useStyles = makeStyles(theme => {
    return {
        portlet: {
            'box-shadow': 'none',
            "& .kt-portlet__head": {
                borderColor: theme.palette.secondary.main,
            }
        },
        portletSection: {
            'box-shadow': 'none',
            "& .kt-portlet__head": {
                borderColor: theme.palette.secondary.main,
                paddingLeft: 0
            }
        },
        statusReady: {
            backgroundColor: '#4caf50',
            color: 'white',
        },
        statusPending: {
            backgroundColor: '#ff9800',
            color: 'white',
        },
        statusFailed: {
            backgroundColor: '#f44336',
            color: 'white',
        }
    }
});

export default function DocumentView({ fields, filteredData, order, sort, contractId, contractListPending, isEditView, vendorListPending, shouldDeleteDocument,
    actions: { setSort, setOrder, getDocumentList, getDocument }
}) {
    const classes = useStyles();
    const ContractsApi = new ContractInfo();
    const [pollTrigger, setPollTrigger] = useState(0);
    const [lastValidStatus, setLastValidStatus] = useState<any>(null);

    useEffect(() => {
        getDocumentList(contractId);
    }, [getDocumentList, contractId]);

    // Fetch document status from AI API
    const { data: aiDocStatus } = useData(async () => {
        if (!contractId) return;
        const statusData = await ContractsApi?.uploadDocumentStatus(contractId);
        return statusData;
    }, [contractId, pollTrigger]); // Add pollTrigger to refetch when it changes

    // Update lastValidStatus when we get new data
    useEffect(() => {
        if (aiDocStatus && Array.isArray(aiDocStatus)) {
            setLastValidStatus(aiDocStatus);
        }
    }, [aiDocStatus]);

    // Use lastValidStatus as fallback to prevent flickering during refetch
    const currentStatus = aiDocStatus || lastValidStatus;

    // Merge CMS documents with AI status by document ID
    const documentsWithStatus = useMemo(() => {
        if (!filteredData || !Array.isArray(filteredData)) return filteredData;

        // Handle case where API might return an object with a status and documents array
        // or just an array of documents
        const statusArray = Array.isArray(currentStatus) 
            ? currentStatus 
            : (currentStatus?.documents || []);
            
        // If the whole request has a status (e.g. "pending"), use it as fallback
        const globalStatus = (!Array.isArray(currentStatus) && currentStatus?.status) || 'pending';

        const merged = filteredData.map(doc => {
            // Use string comparison for IDs to be safe against string/number mismatches
            const aiDoc = statusArray.find(aiD => String(aiD.id) === String(doc.id));
            
            // If document is not in AI API yet or is explicitly pending, it's still being processed
            const status = aiDoc?.status || globalStatus;
            
            return {
                ...doc,
                status: status
            };
        });

        return merged;
    }, [filteredData, currentStatus]);

    // Check if there are any pending documents (including docs not in AI API yet)
    // Note: We only poll for PENDING docs, not FAILED docs
    const hasPendingDocs = useMemo(() => {
        const statusArray = Array.isArray(currentStatus) 
            ? currentStatus 
            : (currentStatus?.documents || []);
            
        const globalStatus = (!Array.isArray(currentStatus) && currentStatus?.status) || null;

        // Check AI API for pending only (not failed)
        const hasInAI = statusArray.some(doc => 
            doc.status === 'pending'
        ) || globalStatus === 'pending';
        
        // Check if any CMS documents are missing from AI API (still processing)
        const missingInAI = filteredData?.some(cmsDoc => 
            !statusArray.find(aiDoc => String(aiDoc.id) === String(cmsDoc.id))
        ) || false;
        
        const hasPending = hasInAI || missingInAI;
        return hasPending;
    }, [currentStatus, filteredData]);

    // Polling mechanism: refetch status every 5 seconds if there are pending docs
    useEffect(() => {
        if (!hasPendingDocs) {
            return;
        }

        const interval = setInterval(() => {
            setPollTrigger(prev => prev + 1);
        }, 30000);

        return () => {
            clearInterval(interval);
        };
    }, [hasPendingDocs]);

    const download = (file, documentName, metadata) => {
        let documentType = ''
        if (metadata) {
            const md = JSON.parse(metadata);
            documentType = md.type;
        }
        const myNewFile = new File([file], documentName, { type: documentType });
        const broswerWindow = window.open();
        broswerWindow.location.href = window.URL.createObjectURL(myNewFile);
    }

    const handleView = (document) => {
        const { documentName, id, metadata } = document;
        getDocument(id, documentName, metadata, download);
    }

    const [retryingDocId, setRetryingDocId] = useState<number | null>(null);

    const handleRetry = async (rowData) => {
        try {
            setRetryingDocId(rowData.id);
            
            // Call the retry API with document data
            await ContractsApi.retryFailedDocuments(contractId, rowData);
            
            // Trigger a poll to fetch updated status
            setPollTrigger(prev => prev + 1);
                        
            // Keep spinner visible until document status changes to 'pending'
            // The polling mechanism will automatically refetch status
            // Once status changes from 'failed', the spinner will be removed
        } catch (error) {
            setRetryingDocId(null);
        }
    }

    // Clear retrying state when document status changes from failed
    useEffect(() => {
        if (retryingDocId && documentsWithStatus) {
            const doc = documentsWithStatus.find(d => d.id === retryingDocId);
            if (doc && doc.status !== 'failed') {
                setRetryingDocId(null);
            }
        }
    }, [documentsWithStatus, retryingDocId]);

    function documentActions(rowData) {
        return (
            <div className="d-flex">
                <IconButton
                    color="primary"
                    className="p-1"
                    aria-label="Edit"
                    onClick={() => handleView(rowData)}
                    size="large"
                >
                    <Tooltip title="View">
                        <PageviewIcon />
                    </Tooltip>
                </IconButton>

                {rowData.status === 'failed' && (
                    <IconButton
                        color="error"
                        className="p-1"
                        aria-label="Retry"
                        onClick={() => handleRetry(rowData)}
                        size="large"
                        disabled={retryingDocId === rowData.id}
                    >
                        <Tooltip title={retryingDocId === rowData.id ? "Retrying..." : "Retry"}>
                            {retryingDocId === rowData.id ? (
                                <CircularProgress size={20} color="error" />
                            ) : (
                                <RefreshIcon />
                            )}
                        </Tooltip>
                    </IconButton>
                )}

                {shouldDeleteDocument && (
                    <ContractDocumentDeleteButton
                        rowData={rowData}
                        getDocuments={() => getDocumentList(contractId)}
                    />
                )}
            </div>
        );
    }

    return (
        <Loadable loading={contractListPending || vendorListPending}>
            <Portlet className={classes.portletSection}>
                {!isEditView && (
                    <PortletHeader
                        className=""
                        title="Documents"
                        showTitle={true}
                        toolbar={<></>}
                    />
                )}
                <PortletBody className="pl-0 pb-0">
                    <Box sx={{ overflowX: 'auto', width: '100%' }}>
                        <DataTable
                            fields={fields}
                            data={documentsWithStatus}
                            sort={sort}
                            onSort={setSort}

                            hasActions={true}
                            renderActionColumn={documentActions}
                        />
                    </Box>
                </PortletBody>
            </Portlet>
        </Loadable>
    );
}